import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  acceptAll,
  CONSENT_EVENTS,
  getConsent,
  hasConsented,
  rejectNonEssential,
  setConsent,
} from "@/lib/cookieConsent";

export function CookieConsentBanner() {
  const { t } = useTranslation("cookies");
  const [showBanner, setShowBanner] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  // Push page content + fixed mobile bottom nav up by the banner's height
  // so it never covers checkout CTAs or the Cart tab at 360px.
  useEffect(() => {
    if (!showBanner) {
      document.body.style.removeProperty("--consent-banner-offset");
      return;
    }
    const el = bannerRef.current;
    if (!el) return;
    const update = () => {
      document.body.style.setProperty(
        "--consent-banner-offset",
        `${el.offsetHeight}px`,
      );
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
      document.body.style.removeProperty("--consent-banner-offset");
    };
  }, [showBanner]);

  // Move keyboard focus to the banner once it appears so keyboard users
  // encounter it immediately without scanning the whole page.
  useEffect(() => {
    if (showBanner && bannerRef.current) {
      bannerRef.current.focus();
    }
  }, [showBanner]);


  // Initial mount: show banner if no prior consent.
  useEffect(() => {
    if (!hasConsented()) {
      setShowBanner(true);
    }
    const current = getConsent();
    if (current) {
      setAnalytics(current.analytics);
      setMarketing(current.marketing);
    }
  }, []);

  // Listen for footer "Cookie settings" link.
  useEffect(() => {
    const handler = () => {
      const current = getConsent();
      setAnalytics(current?.analytics ?? false);
      setMarketing(current?.marketing ?? false);
      setShowPrefs(true);
    };
    window.addEventListener(CONSENT_EVENTS.openPreferences, handler);
    return () =>
      window.removeEventListener(CONSENT_EVENTS.openPreferences, handler);
  }, []);

  const handleAcceptAll = () => {
    acceptAll();
    setAnalytics(true);
    setMarketing(true);
    setShowBanner(false);
    setShowPrefs(false);
  };

  const handleRejectAll = () => {
    rejectNonEssential();
    setAnalytics(false);
    setMarketing(false);
    setShowBanner(false);
    setShowPrefs(false);
  };

  const handleSavePrefs = () => {
    setConsent({ analytics, marketing });
    setShowBanner(false);
    setShowPrefs(false);
  };

  return (
    <>
      {showBanner && (
        <div
          ref={bannerRef}
          role="region"
          aria-label={t("banner.title")}
          tabIndex={-1}
          className="fixed inset-x-0 bottom-0 z-[60] max-h-[75dvh] overflow-y-auto border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 shadow-lg focus:outline-none"
        >
          <div className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:justify-between md:gap-4 md:py-5">

            <div className="flex-1 space-y-1">
              <p className="font-medium text-foreground">
                {t("banner.title")}
              </p>
              <p className="text-sm text-muted-foreground">
                {t("banner.body")}{" "}
                <Link
                  to="/privacy-policy"
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  {t("banner.privacyLink")}
                </Link>
              </p>
            </div>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end md:flex-nowrap">
              <Button
                variant="ghost"
                onClick={() => setShowPrefs(true)}
              >
                {t("banner.preferences")}
              </Button>
              <Button variant="outline" onClick={handleRejectAll}>
                {t("banner.rejectNonEssential")}
              </Button>
              <Button onClick={handleAcceptAll}>{t("banner.acceptAll")}</Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={showPrefs} onOpenChange={setShowPrefs}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("prefs.title")}</DialogTitle>
            <DialogDescription>{t("prefs.description")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex items-start justify-between gap-4 rounded-md border p-3">
              <div className="space-y-1">
                <Label className="font-medium">
                  {t("prefs.necessary.label")}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t("prefs.necessary.desc")}
                </p>
              </div>
              <Switch checked disabled aria-label={t("prefs.necessary.label")} />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-md border p-3">
              <div className="space-y-1">
                <Label htmlFor="consent-analytics" className="font-medium">
                  {t("prefs.analytics.label")}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t("prefs.analytics.desc")}
                </p>
              </div>
              <Switch
                id="consent-analytics"
                checked={analytics}
                onCheckedChange={setAnalytics}
              />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-md border p-3">
              <div className="space-y-1">
                <Label htmlFor="consent-marketing" className="font-medium">
                  {t("prefs.marketing.label")}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t("prefs.marketing.desc")}
                </p>
              </div>
              <Switch
                id="consent-marketing"
                checked={marketing}
                onCheckedChange={setMarketing}
              />
            </div>
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
            <Button variant="ghost" onClick={handleRejectAll}>
              {t("prefs.rejectAll")}
            </Button>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button variant="outline" onClick={handleAcceptAll}>
                {t("prefs.acceptAll")}
              </Button>
              <Button onClick={handleSavePrefs}>{t("prefs.save")}</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default CookieConsentBanner;
